import { useState, useCallback, useEffect } from 'react';
import { toast } from '@/components/ui/use-toast';
import { SocialMediaPost } from '@/lib/api';
import { socialMediaApi } from '../api';

export type ComposerStep = 'compose' | 'adapt' | 'schedule' | 'publish';

export function usePostComposer(existingPost?: SocialMediaPost) {
  const [post, setPost] = useState<SocialMediaPost | null>(existingPost ?? null);
  const [content, setContent] = useState(existingPost?.originalContent ?? '');
  const [title, setTitle] = useState(existingPost?.title ?? '');
  const [notes, setNotes] = useState(existingPost?.notes ?? '');
  const [targetAccountIds, setTargetAccountIds] = useState<string[]>(
    existingPost?.targetAccountIds ?? [],
  );
  const [tags, setTags] = useState<string[]>(existingPost?.tags ?? []);

  // Sync state when existingPost loads asynchronously (e.g. after fetch in ComposerPage)
  useEffect(() => {
    if (!existingPost) return;
    setPost(existingPost);
    setContent(existingPost.originalContent);
    setTitle(existingPost.title ?? '');
    setNotes(existingPost.notes ?? '');
    setTargetAccountIds(existingPost.targetAccountIds);
    setTags(existingPost.tags ?? []);
  }, [existingPost]);

  const [step, setStep] = useState<ComposerStep>('compose');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveDraft = useCallback(
    async (overrideTargetAccountIds?: string[], mediaFileIds?: string[], link?: string | null) => {
      if (!content.trim()) return;
      setIsSaving(true);
      setError(null);
      const accountIds = overrideTargetAccountIds ?? targetAccountIds;
      try {
        if (post) {
          const updated = await socialMediaApi.updateSocialMediaPost(post.postId, {
            originalContent: content,
            targetAccountIds: accountIds,
            mediaFileIds,
            tags,
            title: title || null,
            notes: notes || null,
            link: link !== undefined ? link || null : undefined,
          });
          setPost(updated);
          toast({ description: 'Draft updated.' });
          return updated;
        } else {
          const created = await socialMediaApi.createSocialMediaPost({
            originalContent: content,
            targetAccountIds: accountIds,
            mediaFileIds,
            tags,
            title: title || undefined,
            notes: notes || undefined,
            link: link || undefined,
          });
          setPost(created);
          toast({ description: 'Draft saved.' });
          return created;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save draft';
        setError(message);
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [post, content, targetAccountIds, tags, title, notes],
  );

  const adaptContent = useCallback(
    async (
      platforms?: string[],
      context?: {
        tone?: string[];
        goal?: string[];
        audience?: string;
        link?: string;
        currentPlatformText?: Record<string, string>;
      },
      platformSettings?: Record<string, { format?: string }>,
      /** Explicit post ID — used when the post was just created in the same call chain
       *  so we don't need to wait for a React re-render to get post.postId. */
      overridePostId?: string,
    ) => {
      const postId = overridePostId ?? post?.postId;
      if (!postId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const result = await socialMediaApi.adaptSocialMediaPost(
          postId,
          platforms,
          context,
          platformSettings,
        );
        setPost((prev) => (prev ? { ...prev, platformContent: result.platformContent } : prev));
        toast({
          title: 'Content adapted',
          description: 'AI-generated content is ready for review',
        });
        return result.platformContent;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to adapt content';
        setError(message);
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [post],
  );

  const schedulePost = useCallback(
    async (publishAt: string, overridePostId?: string) => {
      const postId = overridePostId ?? post?.postId;
      if (!postId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const updated = await socialMediaApi.scheduleSocialMediaPost(postId, publishAt);
        setPost(updated);
        toast({
          title: 'Post scheduled',
          description: `Scheduled for ${new Date(publishAt).toLocaleString()}`,
        });
        return updated;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to schedule post';
        setError(message);
        toast({ title: 'Scheduling failed', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [post],
  );

  const publishNow = useCallback(async (postId: string) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await socialMediaApi.publishSocialMediaPostNow(postId);
      const updated = await socialMediaApi.getSocialMediaPost(postId);
      setPost(updated);
      toast({ title: 'Post published', description: 'Your post has been published successfully' });
      return result.results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish post';
      setError(message);
      toast({ title: 'Publishing failed', description: message, variant: 'destructive' });
      return null;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const resetToDraft = useCallback(async () => {
    if (!post) return null;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await socialMediaApi.resetSocialMediaPostToDraft(post.postId);
      setPost(updated);
      toast({ title: 'Post reset to draft', description: 'You can now edit and republish' });
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset post to draft';
      setError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [post]);

  return {
    post,
    content,
    setContent,
    title,
    setTitle,
    notes,
    setNotes,
    targetAccountIds,
    setTargetAccountIds,
    tags,
    setTags,
    step,
    setStep,
    isSaving,
    error,
    saveDraft,
    adaptContent,
    schedulePost,
    publishNow,
    resetToDraft,
  };
}
